import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {Row, RowData} from "@tanstack/react-table";
import {ReactNode} from "react";
import {
    PRINT_INK,
    PRINT_PAGE_PADDING,
    PRINT_PAGE_SIZES
} from "./constants";
import {
    ServerTablePrintColumn,
    ServerTablePrintOrientation,
    ServerTablePrintRenderMode
} from "./types";

interface PrintDocumentHeaderProps {
    title?: string
    subtitle: ReactNode
}

export function PrintDocumentHeader({title, subtitle}: PrintDocumentHeaderProps) {
    return (
        <Stack
            data-print-header
            sx={{pb: 2}}
        >
            <Box sx={{minWidth: 0}}>
                {title && (
                    <Typography sx={{fontSize: 18, fontWeight: 700, lineHeight: 1.2}}>
                        {title}
                    </Typography>
                )}
                {subtitle && (
                    <Box sx={{fontSize: 12, color: PRINT_INK.muted, pt: 0.5, lineHeight: 1.45}}>
                        {subtitle}
                    </Box>
                )}
            </Box>
        </Stack>
    );
}

interface PrintTableProps<TData extends RowData> {
    columns: ServerTablePrintColumn<TData>[]
    rows: Row<TData>[]
    locale: string
    fontSize: number
    renderMode: ServerTablePrintRenderMode
    emptyText: string
}

export function PrintTable<TData extends RowData>({
    columns,
    rows,
    locale,
    fontSize,
    renderMode,
    emptyText
}: PrintTableProps<TData>) {
    const totalWidth = columns.reduce((sum, column) => sum + column.width, 0) || 1;

    return (
        <Box sx={{border: `1px solid ${PRINT_INK.border}`, borderRadius: '6px', overflow: 'hidden'}}>
            <Box
                component="table"
                sx={{width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed'}}
            >
                <colgroup>
                    {columns.map(column => (
                        <col key={column.id} style={{width: `${(column.width / totalWidth) * 100}%`}}/>
                    ))}
                </colgroup>
                <Box component="thead" data-print-thead>
                    <Box component="tr">
                        {columns.map(column => (
                            <Box
                                component="th"
                                key={column.id}
                                sx={{
                                    borderBottom: `1px solid ${PRINT_INK.border}`,
                                    bgcolor: PRINT_INK.headerBg,
                                    color: PRINT_INK.text,
                                    fontSize: fontSize - 1,
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    p: '8px 9px',
                                    textAlign: 'left',
                                    verticalAlign: 'middle',
                                    // Single-line headers keep pagination stable while columns resize.
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {column.label}
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {rows.map(row => (
                        <Box component="tr" key={row.id} data-print-row>
                            {columns.map(column => (
                                <Box
                                    component="td"
                                    key={column.id}
                                    sx={{
                                        borderBottom: `1px solid ${PRINT_INK.rowBorder}`,
                                        p: '8px 9px',
                                        verticalAlign: 'top',
                                        fontSize,
                                        lineHeight: 1.35,
                                        color: PRINT_INK.text,
                                        overflowWrap: 'anywhere',
                                        ...(renderMode === 'original' && {
                                            '& [data-element-chip]': {
                                                height: '22px !important',
                                                py: '0px !important',
                                                px: '4px !important',
                                                gap: '3px !important',
                                                alignItems: 'center !important',
                                                whiteSpace: 'nowrap !important',
                                                '@media print': {
                                                    backgroundColor: 'var(--element-chip-background) !important',
                                                    color: 'var(--element-chip-color) !important',
                                                    border: '1px solid var(--element-chip-border-color) !important',
                                                    boxShadow: 'var(--element-chip-box-shadow) !important'
                                                }
                                            },
                                            '& [data-element-chip-icon]': {
                                                mt: '0px !important',
                                                transform: 'scale(0.85) !important'
                                            },
                                            '& [data-element-chip-label]': {
                                                fontSize: '11px !important',
                                                lineHeight: '14px !important',
                                                mt: '0px !important',
                                                overflow: 'hidden !important',
                                                textOverflow: 'ellipsis !important',
                                                whiteSpace: 'nowrap !important'
                                            },
                                            '& .MuiChip-label': {
                                                px: '4px'
                                            }
                                        })
                                    }}
                                >
                                    {renderMode === 'original' && column.renderOriginal
                                        ? column.renderOriginal(row, {locale})
                                        : column.render?.(row, {locale})}
                                </Box>
                            ))}
                        </Box>
                    ))}

                    {rows.length === 0 && (
                        <Box component="tr">
                            <Box
                                component="td"
                                colSpan={Math.max(1, columns.length)}
                                sx={{p: 3, textAlign: 'center', color: PRINT_INK.muted, fontSize}}
                            >
                                {emptyText}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

interface PrintPageSheetProps {
    orientation: ServerTablePrintOrientation
    isLast: boolean
    pageLabel?: string
    children: ReactNode
}

export function PrintPageSheet({orientation, isLast, pageLabel, children}: PrintPageSheetProps) {
    const size = PRINT_PAGE_SIZES[orientation];

    return (
        <Box
            className="ServerTablePrintPage"
            sx={{
                position: 'relative',
                width: `${size.width}px`,
                height: `${size.height}px`,
                p: `${PRINT_PAGE_PADDING}px`,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'common.white',
                color: PRINT_INK.text,
                // The print stylesheet removes preview-only chrome.
                boxShadow: '0 2px 10px rgba(15, 23, 42, 0.16)',
                // Avoiding a final break prevents an extra blank sheet in some browsers.
                breakAfter: isLast ? 'auto' : 'page',
                breakInside: 'avoid'
            }}
        >
            {pageLabel && (
                <Box
                    sx={{
                        // The label occupies the padding band and costs no row budget.
                        position: 'absolute',
                        top: '13px',
                        right: `${PRINT_PAGE_PADDING}px`,
                        fontSize: 10,
                        lineHeight: 1,
                        color: PRINT_INK.muted
                    }}
                >
                    {pageLabel}
                </Box>
            )}
            {children}
        </Box>
    );
}
